import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Popconfirm, Space, Checkbox, Card, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { userApi, batchApi, globalSettingsApi } from '../../services/api';
import type { User, CreateUserRequest, Batch, FilterRule } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [filterOptions, setFilterOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadData();
    loadFilterOptions();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, batchesData] = await Promise.all([
        userApi.list(),
        batchApi.list(),
      ]);
      setUsers(usersData);
      setBatches(batchesData);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const options = await globalSettingsApi.getFilterRuleOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({
      role: 'user',
      assignedBatches: [],
      filterRules: [],
    });
    setModalVisible(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      role: user.role,
      assignedBatches: user.assignedBatches || [],
      filterRules: user.filterRules || [],
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await userApi.delete(id);
      message.success('User deleted successfully');
      loadData();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data: CreateUserRequest = {
        username: values.username,
        password: values.password,
        role: values.role,
        assignedBatches: values.assignedBatches || [],
        filterRules: values.filterRules || [],
      };

      if (editingUser) {
        // Don't send password if it's empty
        if (!values.password) {
          delete (data as any).password;
        }
        await userApi.update(editingUser.id, data);
        message.success('User updated successfully');
      } else {
        await userApi.create(data);
        message.success('User created successfully');
      }

      setModalVisible(false);
      form.resetFields();
      loadData();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to save user');
    }
  };

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => role.toUpperCase(),
    },
    {
      title: 'Assigned Batches',
      dataIndex: 'assignedBatches',
      key: 'assignedBatches',
      render: (batchIds: string[]) => {
        if (!batchIds || batchIds.length === 0) return '-';
        const batchNames = batchIds.map(id => {
          const batch = (batches || []).find(b => b.id === id);
          return batch?.name || 'Unknown';
        });
        return batchNames.join(', ');
      },
    },
    {
      title: 'Filter Rules',
      dataIndex: 'filterRules',
      key: 'filterRules',
      render: (rules: FilterRule[]) => rules?.length || 0,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: User) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1>User Management</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Create User
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} users`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
      />

      <Modal
        title={editingUser ? 'Edit User' : 'Create User'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Please enter username' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="password"
            label={editingUser ? "Password (leave blank to keep current)" : "Password"}
            rules={editingUser ? [] : [{ required: true, min: 6, message: 'Password must be at least 6 characters' }]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select>
              <Select.Option value="user">User</Select.Option>
              <Select.Option value="admin">Admin</Select.Option>
              {currentUser?.role === 'superadmin' && (
                <Select.Option value="superadmin">Super Admin</Select.Option>
              )}
            </Select>
          </Form.Item>

          <Form.Item
            label="Assigned Batches"
          >
            <Form.Item name="assignedBatches" noStyle>
              <Checkbox.Group style={{ width: '100%' }}>
                <Row gutter={[16, 16]}>
                  {(batches || []).map(batch => (
                    <Col span={12} key={batch.id}>
                      <Checkbox value={batch.id}>
                        {batch.name} ({batch.country}, {batch.numbers?.length || 0} numbers)
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Form.Item>
          </Form.Item>

          <Card title="Filter Rules" size="small" style={{ marginTop: 16 }}>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>
              Filter rules determine which SMS messages this user can see. Messages matching ANY rule will be shown (OR logic).
              <br />
              <strong>Note:</strong> Only messages that also match the global master filter (configured by SuperAdmin) will be visible.
            </p>
            <Form.List name="filterRules">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      size="small"
                      style={{ marginBottom: 8 }}
                      extra={
                        <Button
                          type="link"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        >
                          Remove
                        </Button>
                      }
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'field']}
                            label="Field"
                            rules={[{ required: true, message: 'Required' }]}
                          >
                            <Select placeholder="Select field">
                              <Select.Option value="from">FROM</Select.Option>
                              <Select.Option value="content">CONTENT</Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={16}>
                          <Form.Item
                            {...restField}
                            name={[name, 'contains']}
                            label="Contains"
                            rules={[{ required: true, message: 'Required' }]}
                          >
                            <Select
                              placeholder="Select keyword to filter"
                              showSearch
                              filterOption={(input, option) => {
                                const label = option?.children || '';
                                return String(label).toLowerCase().includes(input.toLowerCase());
                              }}
                            >
                              {(filterOptions || []).map(opt => (
                                <Select.Option key={opt} value={opt.toLowerCase()}>
                                  {opt}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={() => add()} block>
                    + Add Filter Rule
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
